import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { emailsApi } from "../lib/emailsApi";

/**
 * AdminEmails — cliente de e-mail (webmail) do Painel Mestre, ligado à caixa
 * de suporte via IMAP/SMTP. Ferramenta isolada: não conversa com
 * o módulo WhatsApp do tenant nem com nenhuma outra parte do CRM.
 *
 * Layout de dois/três painéis espelhando a aba "Conversas" do WhatsApp:
 * pastas · lista · leitura/resposta.
 */

const FOLDER_ICON = {
    inbox: "fa-inbox",
    sent: "fa-paper-plane",
    drafts: "fa-file-pen",
    archive: "fa-box-archive",
    spam: "fa-triangle-exclamation",
    trash: "fa-trash-can",
    outbox: "fa-arrow-up-from-bracket",
    all: "fa-layer-group",
    flagged: "fa-flag",
    folder: "fa-folder",
};
const FOLDER_LABEL = {
    inbox: "Entrada",
    sent: "Enviados",
    drafts: "Rascunhos",
    archive: "Arquivo",
    spam: "Spam",
    trash: "Lixeira",
    outbox: "Saída",
    all: "Todos",
};

const folderLabel = (f) => FOLDER_LABEL[f.role] || String(f.name || "").split("/").pop();

const escapeHtml = (s) =>
    String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const textToHtml = (t) => escapeHtml(t).replace(/\n/g, "<br>");

const reSubject = (s) => {
    const clean = String(s || "").trim();
    return /^re:/i.test(clean) ? clean : `Re: ${clean}`;
};

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    if (d.getFullYear() === now.getFullYear())
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "");
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const fmtFull = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d) ? "" : d.toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" });
};

const initials = (s) =>
    String(s || "?")
        .replace(/[<>"]/g, "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "?";

const AdminEmails = () => {
    const [status, setStatus] = useState(null); // {connected, configured, address, error, last_sync}
    const [folders, setFolders] = useState([]);
    const [activeFolder, setActiveFolder] = useState("INBOX");
    const [lastSync, setLastSync] = useState(null);

    const [emails, setEmails] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [search, setSearch] = useState("");

    const [selected, setSelected] = useState(null);
    const [msgLoading, setMsgLoading] = useState(false);

    const [compose, setCompose] = useState(null); // {to, subject, body, replyUid, replyFolder} | null
    const [sending, setSending] = useState(false);
    const [reply, setReply] = useState("");
    const [replying, setReplying] = useState(false);

    // mobile: qual painel aparece (folders/list/message)
    const [mobilePane, setMobilePane] = useState("list");

    const activeFolderObj = useMemo(
        () => folders.find((f) => f.name === activeFolder),
        [folders, activeFolder]
    );
    const activeRole = activeFolderObj?.role;

    // ─── loaders ───────────────────────────────────────────────────────
    const loadStatus = useCallback(async () => {
        try {
            const s = await emailsApi.status();
            setStatus(s);
            if (s?.last_sync) setLastSync(s.last_sync);
        } catch (e) {
            setStatus({ connected: false, configured: true, error: e.message });
        }
    }, []);

    const loadFolders = useCallback(async () => {
        try {
            const r = await emailsApi.folders();
            setFolders(r?.folders || []);
            setLastSync(new Date().toISOString());
        } catch (e) {
            // status banner já comunica o erro de conexão
        }
    }, []);

    const loadList = useCallback(async (folder, pg, term) => {
        setListLoading(true);
        try {
            const r = await emailsApi.list(folder, pg, term);
            setEmails(r?.items || []);
            setTotal(r?.total || 0);
            setHasMore(!!r?.has_more);
            setPage(r?.page || pg);
        } catch (e) {
            setEmails([]);
            setTotal(0);
            setHasMore(false);
            toast.error("Falha ao carregar e-mails", { description: e.message });
        } finally {
            setListLoading(false);
        }
    }, []);

    // primeiro load
    useEffect(() => {
        loadStatus();
        loadFolders();
        loadList("INBOX", 1, "");
    }, [loadStatus, loadFolders, loadList]);

    // poll leve do status (mantém "Conectado" e "última sync" frescos)
    useEffect(() => {
        const t = setInterval(loadStatus, 60000);
        return () => clearInterval(t);
    }, [loadStatus]);

    // busca com debounce (pula o disparo inicial — "primeiro load" já cobriu)
    const searchRef = useRef(search);
    searchRef.current = search;
    const didMount = useRef(false);
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        const t = setTimeout(() => {
            loadList(activeFolder, 1, searchRef.current);
        }, 420);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const selectFolder = (name) => {
        if (name === activeFolder) {
            setMobilePane("list");
            return;
        }
        setActiveFolder(name);
        setSelected(null);
        setSearch("");
        setPage(1);
        setMobilePane("list");
        loadList(name, 1, "");
    };

    const openMessage = async (uid) => {
        setMsgLoading(true);
        setMobilePane("message");
        try {
            const wasUnseen = emails.some((e) => e.uid === uid && !e.seen);
            const m = await emailsApi.message(activeFolder, uid);
            setSelected(m);
            // marca como lido na lista + decrementa contador da pasta (só se era não lido)
            setEmails((list) => list.map((e) => (e.uid === uid ? { ...e, seen: true } : e)));
            if (wasUnseen) {
                setFolders((fs) =>
                    fs.map((f) =>
                        f.name === activeFolder ? { ...f, unread: Math.max(0, (f.unread || 0) - 1) } : f
                    )
                );
            }
        } catch (e) {
            toast.error("Falha ao abrir e-mail", { description: e.message });
            setMobilePane("list");
        } finally {
            setMsgLoading(false);
        }
    };

    const refreshAll = () => {
        loadStatus();
        loadFolders();
        loadList(activeFolder, page, searchRef.current);
    };

    const moveToTrash = async (uid) => {
        try {
            await emailsApi.trash(activeFolder, uid);
            setEmails((list) => list.filter((e) => e.uid !== uid));
            setTotal((t) => Math.max(0, t - 1));
            if (selected?.uid === uid) {
                setSelected(null);
                setMobilePane("list");
            }
            toast.success("E-mail movido para a Lixeira");
            loadFolders();
        } catch (e) {
            toast.error("Falha ao mover para a Lixeira", { description: e.message });
        }
    };

    // ─── compose / reply ───────────────────────────────────────────────
    const openCompose = (preset) =>
        setCompose({ to: "", subject: "", body: "", replyUid: null, replyFolder: null, ...(preset || {}) });

    const doSend = async () => {
        if (!compose) return;
        const to = compose.to.trim();
        if (!to || !to.includes("@")) {
            toast.error("Informe um destinatário válido");
            return;
        }
        setSending(true);
        try {
            const res = await emailsApi.send({
                to,
                subject: compose.subject.trim(),
                html_body: textToHtml(compose.body),
                in_reply_to_uid: compose.replyUid || null,
                in_reply_to_folder: compose.replyFolder || null,
            });
            toast.success("E-mail enviado", {
                description: res?.saved_to_sent ? "Cópia salva em Enviados" : "Enviado (cópia em Enviados não confirmada)",
            });
            setCompose(null);
            loadFolders();
            if (activeRole === "sent") loadList(activeFolder, 1, "");
        } catch (e) {
            toast.error("Falha ao enviar", { description: e.message });
        } finally {
            setSending(false);
        }
    };

    const doReply = async () => {
        if (!selected || !reply.trim()) return;
        setReplying(true);
        try {
            const res = await emailsApi.send({
                to: selected.from_email,
                subject: reSubject(selected.subject),
                html_body: textToHtml(reply),
                in_reply_to_uid: selected.uid,
                in_reply_to_folder: activeFolder,
            });
            toast.success("Resposta enviada", {
                description: res?.saved_to_sent ? "Cópia salva em Enviados" : undefined,
            });
            setReply("");
            loadFolders();
        } catch (e) {
            toast.error("Falha ao responder", { description: e.message });
        } finally {
            setReplying(false);
        }
    };

    // ─── render helpers ────────────────────────────────────────────────
    const connBadge = () => {
        if (status && status.configured === false)
            return (
                <span data-testid="admin-emails-conn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-xs text-slate-300">
                    <i className="fa-solid fa-plug-circle-xmark text-slate-400"></i>
                    Não configurado
                </span>
            );
        if (status?.connected)
            return (
                <span data-testid="admin-emails-conn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-300">
                    <i className="fa-solid fa-circle text-[8px] text-emerald-400 animate-pulse"></i>
                    Conectado
                </span>
            );
        return (
            <span data-testid="admin-emails-conn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/30 bg-red-500/10 text-xs text-red-300" title={status?.error || ""}>
                <i className="fa-solid fa-triangle-exclamation text-red-400"></i>
                Erro de conexão
            </span>
        );
    };

    // função de render (NÃO um componente) — evita remontar a coluna de pastas
    // a cada tecla digitada na busca.
    const renderFolderPanel = (className = "") => (
        <div className={`rounded-2xl border border-white/10 bg-slate-900/40 flex flex-col overflow-hidden ${className}`}>
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Pastas</span>
                <button
                    data-testid="admin-emails-refresh"
                    onClick={refreshAll}
                    title="Atualizar"
                    className="h-7 w-7 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/20 flex items-center justify-center transition-all"
                >
                    <i className="fa-solid fa-rotate text-[11px]"></i>
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {folders.length === 0 && (
                    <div className="text-xs text-slate-500 p-3">
                        {status?.connected ? "Nenhuma pasta." : "Sem conexão com a caixa."}
                    </div>
                )}
                {folders.map((f) => {
                    const active = f.name === activeFolder;
                    return (
                        <button
                            key={f.name}
                            data-testid={`admin-emails-folder-${f.role}`}
                            onClick={() => selectFolder(f.name)}
                            disabled={f.selectable === false}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all ${
                                active
                                    ? "bg-indigo-500/15 border border-indigo-500/30 text-white"
                                    : "border border-transparent text-slate-300 hover:bg-white/5"
                            } ${f.selectable === false ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                            <i className={`fa-solid ${FOLDER_ICON[f.role] || FOLDER_ICON.folder} text-xs w-4 text-center ${active ? "text-indigo-300" : "text-slate-500"}`}></i>
                            <span className="flex-1 text-left truncate">{folderLabel(f)}</span>
                            {f.unread > 0 && (
                                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center">
                                    {f.unread > 99 ? "99+" : f.unread}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const showList = mobilePane === "list";
    const showMsg = mobilePane === "message";

    return (
        <div className="space-y-4" data-testid="admin-emails">
            {/* Header / status */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Plataforma</div>
                    <h1 className="salon-heading text-3xl text-white mt-1">E-mail</h1>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {status?.address && <span className="font-mono text-slate-400">{status.address}</span>}
                        {total > 0 && (
                            <>
                                <span className="text-slate-700">·</span>
                                <span>{emails.length} de {total}</span>
                            </>
                        )}
                        {lastSync && (
                            <>
                                <span className="text-slate-700">·</span>
                                <span>última sync {fmtDate(lastSync)}</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {connBadge()}
                    <button
                        data-testid="admin-emails-compose"
                        onClick={() => openCompose()}
                        className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-4 py-2 text-sm transition-all"
                    >
                        <i className="fa-solid fa-pen-to-square text-xs"></i>
                        Escrever
                    </button>
                </div>
            </div>

            {status && !status.connected && status.configured !== false && (
                <div data-testid="admin-emails-error-banner" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                    {status.error || "Não foi possível conectar ao servidor de e-mail."}
                </div>
            )}
            {status && status.configured === false && (
                <div data-testid="admin-emails-notconfigured-banner" className="rounded-xl border border-slate-500/30 bg-slate-500/10 px-4 py-3 text-sm text-slate-300">
                    <i className="fa-solid fa-circle-info mr-2"></i>
                    Cliente de e-mail não configurado neste ambiente (variáveis <span className="font-mono">MAIL_*</span>).
                </div>
            )}

            {/* Painéis */}
            <div className="grid lg:grid-cols-[200px_minmax(300px,360px)_1fr] gap-4 h-[calc(100vh-260px)] min-h-[520px]">
                {/* Pastas — desktop sempre; mobile só quando mobilePane === folders */}
                {renderFolderPanel(`${mobilePane === "folders" ? "flex" : "hidden"} lg:flex`)}

                {/* Lista */}
                <div className={`${showList ? "flex" : "hidden"} lg:flex rounded-2xl border border-white/10 bg-slate-900/40 flex-col overflow-hidden`}>
                    <div className="p-3 border-b border-white/5 space-y-2">
                        <div className="flex items-center gap-2 lg:hidden">
                            <button
                                data-testid="admin-emails-mobile-folders"
                                onClick={() => setMobilePane("folders")}
                                className="flex items-center gap-2 text-xs text-slate-300 border border-white/10 rounded-lg px-2.5 py-1.5"
                            >
                                <i className="fa-solid fa-folder-tree text-[11px]"></i>
                                {activeFolderObj ? folderLabel(activeFolderObj) : "Pastas"}
                            </button>
                        </div>
                        <div className="relative">
                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
                            <input
                                data-testid="admin-emails-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar nesta pasta…"
                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg pl-9 pr-8 py-2 text-sm outline-none focus:border-indigo-500/40"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                >
                                    <i className="fa-solid fa-xmark text-xs"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto" data-testid="admin-emails-list">
                        {listLoading ? (
                            <div className="p-6 text-center text-xs text-slate-500">Carregando…</div>
                        ) : emails.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-500">
                                {search ? "Nenhum e-mail encontrado para essa busca." : "Pasta vazia."}
                            </div>
                        ) : (
                            emails.map((e) => {
                                const active = selected?.uid === e.uid;
                                return (
                                    <button
                                        key={e.uid}
                                        data-testid={`admin-emails-item-${e.uid}`}
                                        onClick={() => openMessage(e.uid)}
                                        className={`w-full text-left px-3 py-3 border-b border-white/5 flex gap-3 transition-all ${
                                            active ? "bg-indigo-500/10" : "hover:bg-slate-800/40"
                                        }`}
                                    >
                                        <div className={`h-9 w-9 rounded-full border flex items-center justify-center text-[11px] font-bold shrink-0 ${
                                            e.seen ? "bg-slate-800 border-white/10 text-slate-400" : "bg-indigo-500/20 border-indigo-500/40 text-indigo-200"
                                        }`}>
                                            {initials(e.from_name || e.from_email)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <div className={`text-sm truncate ${e.seen ? "text-slate-300" : "text-white font-semibold"}`}>
                                                    {e.from_name || e.from_email || "(sem remetente)"}
                                                </div>
                                                <div className="text-[10px] text-slate-500 shrink-0">{fmtDate(e.date)}</div>
                                            </div>
                                            <div className={`text-xs truncate mt-0.5 flex items-center gap-1.5 ${e.seen ? "text-slate-400" : "text-slate-200"}`}>
                                                {!e.seen && <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0"></span>}
                                                {e.has_attachments && <i className="fa-solid fa-paperclip text-[9px] text-slate-500"></i>}
                                                <span className="truncate">{e.subject}</span>
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate mt-0.5">{e.preview}</div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {(total > emails.length || page > 1) && !listLoading && emails.length > 0 && (
                        <div className="p-2 border-t border-white/5 flex items-center justify-between text-xs">
                            <button
                                data-testid="admin-emails-prev"
                                disabled={page <= 1}
                                onClick={() => loadList(activeFolder, page - 1, searchRef.current)}
                                className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 disabled:opacity-30 hover:border-white/20"
                            >
                                <i className="fa-solid fa-chevron-left mr-1"></i>Anteriores
                            </button>
                            <span className="text-slate-500">Página {page}</span>
                            <button
                                data-testid="admin-emails-next"
                                disabled={!hasMore}
                                onClick={() => loadList(activeFolder, page + 1, searchRef.current)}
                                className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 disabled:opacity-30 hover:border-white/20"
                            >
                                Próximos<i className="fa-solid fa-chevron-right ml-1"></i>
                            </button>
                        </div>
                    )}
                </div>

                {/* Leitura / resposta */}
                <div className={`${showMsg ? "flex" : "hidden"} lg:flex rounded-2xl border border-white/10 bg-slate-900/40 flex-col overflow-hidden`}>
                    {msgLoading ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Carregando e-mail…</div>
                    ) : !selected ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-2">
                            <i className="fa-solid fa-envelope-open text-6xl text-indigo-500/20"></i>
                            <div className="text-sm">Selecione um e-mail</div>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-white/5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-base text-white font-semibold truncate">{selected.subject}</div>
                                        <div className="text-xs text-slate-400 mt-1 truncate">
                                            <span className="text-slate-200">{selected.from_name || selected.from_email}</span>
                                            {selected.from_name && <span className="text-slate-500"> · {selected.from_email}</span>}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {selected.to?.length ? `para ${selected.to.join(", ")}` : ""} · {fmtFull(selected.date)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                            data-testid="admin-emails-back"
                                            onClick={() => { setSelected(null); setMobilePane("list"); }}
                                            className="lg:hidden h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-white flex items-center justify-center"
                                        >
                                            <i className="fa-solid fa-arrow-left text-xs"></i>
                                        </button>
                                        <button
                                            data-testid="admin-emails-reply-btn"
                                            onClick={() =>
                                                openCompose({
                                                    to: selected.from_email,
                                                    subject: reSubject(selected.subject),
                                                    replyUid: selected.uid,
                                                    replyFolder: activeFolder,
                                                })
                                            }
                                            title="Responder"
                                            className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-indigo-300 hover:border-indigo-500/40 flex items-center justify-center"
                                        >
                                            <i className="fa-solid fa-reply text-xs"></i>
                                        </button>
                                        <button
                                            data-testid="admin-emails-trash-btn"
                                            onClick={() => moveToTrash(selected.uid)}
                                            title="Mover para a Lixeira"
                                            className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-500/40 flex items-center justify-center"
                                        >
                                            <i className="fa-solid fa-trash-can text-xs"></i>
                                        </button>
                                    </div>
                                </div>
                                {selected.attachments?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {selected.attachments.map((a, i) => (
                                            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 border border-white/10 rounded-md px-2 py-1">
                                                <i className="fa-solid fa-paperclip text-[9px]"></i>
                                                {a.filename || "anexo"}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto p-3">
                                {selected.html ? (
                                    <iframe
                                        title="conteúdo do e-mail"
                                        data-testid="admin-emails-body"
                                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                                        srcDoc={selected.html}
                                        className="w-full h-full min-h-[320px] bg-white rounded-lg border border-white/10"
                                    />
                                ) : (
                                    <pre data-testid="admin-emails-body" className="whitespace-pre-wrap text-sm text-slate-200 font-sans leading-relaxed">
                                        {selected.text || "(sem conteúdo)"}
                                    </pre>
                                )}
                            </div>

                            {activeRole !== "sent" && activeRole !== "drafts" && (
                                <div className="p-3 border-t border-white/5">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">
                                        Resposta rápida · <span className="text-slate-400 normal-case tracking-normal">{reSubject(selected.subject)}</span>
                                    </div>
                                    <div className="flex gap-2 items-end">
                                        <textarea
                                            data-testid="admin-emails-reply-input"
                                            value={reply}
                                            onChange={(e) => setReply(e.target.value)}
                                            rows={2}
                                            placeholder={`Responder para ${selected.from_email}…`}
                                            className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/40 resize-none"
                                        />
                                        <button
                                            data-testid="admin-emails-reply-send"
                                            onClick={doReply}
                                            disabled={replying || !reply.trim()}
                                            className="h-10 px-4 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white text-sm font-semibold flex items-center gap-2 transition-all"
                                        >
                                            <i className={`fa-solid ${replying ? "fa-spinner fa-spin" : "fa-paper-plane"} text-xs`}></i>
                                            Enviar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {compose &&
                createPortal(
                    <div
                        data-testid="admin-emails-compose-modal"
                        onClick={() => !sending && setCompose(null)}
                        className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                    >
                        <div
                            onClick={(ev) => ev.stopPropagation()}
                            className="w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-950 shadow-2xl flex flex-col max-h-[90vh]"
                        >
                            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                                <h3 className="salon-heading text-lg text-white">
                                    {compose.replyUid ? "Responder e-mail" : "Novo e-mail"}
                                </h3>
                                <button
                                    onClick={() => !sending && setCompose(null)}
                                    className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-white flex items-center justify-center"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                            <div className="p-5 space-y-3 overflow-y-auto">
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Para</span>
                                    <input
                                        data-testid="admin-emails-compose-to"
                                        value={compose.to}
                                        onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                                        placeholder="destinatario@email.com"
                                        className="mt-1 w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/40"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Assunto</span>
                                    <input
                                        data-testid="admin-emails-compose-subject"
                                        value={compose.subject}
                                        onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                                        placeholder="Assunto"
                                        className="mt-1 w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/40"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Mensagem</span>
                                    <textarea
                                        data-testid="admin-emails-compose-body"
                                        value={compose.body}
                                        onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
                                        rows={10}
                                        placeholder="Escreva sua mensagem…"
                                        className="mt-1 w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500/40 resize-none"
                                    />
                                </label>
                            </div>
                            <div className="px-5 py-4 border-t border-white/5 flex items-center justify-end gap-2">
                                <button
                                    onClick={() => !sending && setCompose(null)}
                                    className="px-4 py-2 rounded-xl border border-white/10 text-slate-300 hover:text-white text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    data-testid="admin-emails-compose-send"
                                    onClick={doSend}
                                    disabled={sending}
                                    className="px-5 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white text-sm font-semibold flex items-center gap-2"
                                >
                                    <i className={`fa-solid ${sending ? "fa-spinner fa-spin" : "fa-paper-plane"} text-xs`}></i>
                                    Enviar
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default AdminEmails;
